import { useState, useEffect, useCallback } from 'react';
import { supabase } from '@/lib/supabase';
import { useRealtime } from '@/hooks/useRealtime';
import { useAuth } from '@/hooks/useAuth';
import AuthGuard from '@/components/feature/AuthGuard';
import AdminLayout from '../components/AdminLayout';

interface Domain {
  id: string;
  name: string;
  description: string;
  status: boolean;
  icon: string | null;
  color: string | null;
  sort_order: number;
  created_at: string;
}

const colorOptions = [
  { value: 'primary', label: 'Primary', class: 'bg-primary-500' },
  { value: 'accent', label: 'Accent', class: 'bg-accent-500' },
  { value: 'secondary', label: 'Secondary', class: 'bg-secondary-500' },
];

export default function AdminDomains() {
  const { profile } = useAuth();
  const [domains, setDomains] = useState<Domain[]>([]);
  const [psCounts, setPsCounts] = useState<Record<string, number>>({});
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [search, setSearch] = useState('');
  const [showModal, setShowModal] = useState(false);
  const [editId, setEditId] = useState<string | null>(null);
  const [form, setForm] = useState({ name: '', description: '', status: true, icon: 'ri-lightbulb-line', color: 'primary', sort_order: 0 });
  const [saving, setSaving] = useState(false);

  const loadDomains = useCallback(async () => {
    try {
      setLoading(true);
      const [domRes, psRes] = await Promise.all([
        supabase.from('domains').select('*').order('sort_order', { ascending: true }).order('name', { ascending: true }),
        supabase.from('problem_statements').select('domain_id'),
      ]);
      if (domRes.error) throw domRes.error;
      if (psRes.error) throw psRes.error;

      const counts: Record<string, number> = {};
      (psRes.data || []).forEach((ps) => {
        if (ps.domain_id) counts[ps.domain_id] = (counts[ps.domain_id] || 0) + 1;
      });

      setDomains(domRes.data || []);
      setPsCounts(counts);
      setError('');
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to load domains');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDomains();
  }, [loadDomains]);

  useRealtime<Domain>({
    table: 'domains',
    onAny: () => {
      loadDomains();
    },
  });

  const filtered = domains.filter((d) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return d.name.toLowerCase().includes(q) || (d.description || '').toLowerCase().includes(q);
  });

  const toggleStatus = async (id: string, currentStatus: boolean) => {
    try {
      await supabase.from('domains').update({ status: !currentStatus }).eq('id', id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to update domain');
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await supabase.from('domains').delete().eq('id', id);
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to delete');
    }
  };

  const handleSave = async () => {
    if (!form.name.trim()) return;
    setSaving(true);
    try {
      if (editId) {
        await supabase.from('domains').update({
          name: form.name.trim(),
          description: form.description.trim(),
          status: form.status,
          icon: form.icon,
          color: form.color,
          sort_order: form.sort_order,
          updated_at: new Date().toISOString(),
        }).eq('id', editId);
      } else {
        await supabase.from('domains').insert({
          name: form.name.trim(),
          description: form.description.trim(),
          status: form.status,
          icon: form.icon,
          color: form.color,
          sort_order: form.sort_order,
        });

        await supabase.from('audit_logs').insert({
          user_id: profile?.id,
          user_name: profile?.full_name || 'Admin',
          role: profile?.role,
          action: 'Domain Created',
          module: 'Domains',
        });
      }
      setShowModal(false);
      setEditId(null);
      setForm({ name: '', description: '', status: true, icon: 'ri-lightbulb-line', color: 'primary', sort_order: 0 });
    } catch (err: unknown) {
      setError(err instanceof Error ? err.message : 'Failed to save domain');
    } finally {
      setSaving(false);
    }
  };

  const openEdit = (d: Domain) => {
    setEditId(d.id);
    setForm({
      name: d.name,
      description: d.description || '',
      status: d.status,
      icon: d.icon || 'ri-lightbulb-line',
      color: d.color || 'primary',
      sort_order: d.sort_order || 0,
    });
    setShowModal(true);
  };

  return (
    <AuthGuard requiredRole="admin">
      <AdminLayout title="Domains" breadcrumbs={[{ label: 'Dashboard', href: '/dashboard/admin' }, { label: 'Domains' }]}>
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-red-50 border border-red-200 text-red-600 text-sm flex items-center justify-between">
            <span>{error}</span>
            <button onClick={() => setError('')} className="cursor-pointer"><i className="ri-close-line"></i></button>
          </div>
        )}

        <div className="flex items-center justify-between mb-5">
          <div className="relative w-72">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400 text-sm"></i>
            <input type="text" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search domains..." className="w-full pl-9 pr-4 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 focus:ring-1 focus:ring-primary-200 outline-none transition-colors" />
          </div>
          <button onClick={() => { setEditId(null); setForm({ name: '', description: '', status: true, icon: 'ri-lightbulb-line', color: 'primary', sort_order: 0 }); setShowModal(true); }} className="px-4 py-2.5 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap flex items-center gap-2">
            <i className="ri-add-line"></i>
            Add Domain
          </button>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="w-10 h-10 rounded-full border-2 border-primary-500 border-t-transparent animate-spin"></div>
          </div>
        ) : domains.length === 0 ? (
          <div className="py-16 text-center">
            <div className="w-16 h-16 mx-auto mb-4 rounded-full bg-background-100 flex items-center justify-center">
              <i className="ri-stack-line text-2xl text-foreground-300"></i>
            </div>
            <p className="text-foreground-400 text-sm">No domains available</p>
          </div>
        ) : (
          <div className="bg-background-50 border border-background-200/70 rounded-2xl overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-background-200/70 bg-background-100/50">
                    {['Domain Name', 'Description', 'Icon', 'Color', 'PS Count', 'Order', 'Status', 'Created', 'Actions'].map((l) => (
                      <th key={l} className="px-4 py-3 text-left text-xs font-semibold text-foreground-500 whitespace-nowrap">{l}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {filtered.map((d) => (
                    <tr key={d.id} className="border-b border-background-100 hover:bg-background-50/50 transition-colors">
                      <td className="px-4 py-3 font-medium text-foreground-900">{d.name}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs max-w-xs truncate">{d.description}</td>
                      <td className="px-4 py-3">
                        <div className="w-8 h-8 rounded-lg bg-background-100 flex items-center justify-center">
                          <i className={`${d.icon || 'ri-stack-line'} text-sm text-foreground-600`}></i>
                        </div>
                      </td>
                      <td className="px-4 py-3">
                        <div className={`w-4 h-4 rounded-full ${d.color === 'primary' ? 'bg-primary-500' : d.color === 'accent' ? 'bg-accent-500' : 'bg-secondary-500'}`}></div>
                      </td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{psCounts[d.id] || 0}</td>
                      <td className="px-4 py-3 text-foreground-600 text-xs">{d.sort_order}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <button onClick={() => toggleStatus(d.id, d.status)} className={`relative w-10 h-5 rounded-full transition-colors cursor-pointer ${d.status ? 'bg-accent-500' : 'bg-background-300'}`}>
                          <div className={`absolute top-0.5 w-4 h-4 rounded-full bg-white transition-transform ${d.status ? 'translate-x-5' : 'translate-x-0.5'}`}></div>
                        </button>
                      </td>
                      <td className="px-4 py-3 text-foreground-500 whitespace-nowrap text-xs">{new Date(d.created_at).toLocaleDateString('en-IN')}</td>
                      <td className="px-4 py-3 whitespace-nowrap">
                        <div className="flex items-center gap-1">
                          <button onClick={() => openEdit(d)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-accent-50 text-foreground-400 hover:text-accent-600 transition-colors cursor-pointer" title="Edit">
                            <i className="ri-edit-line text-sm"></i>
                          </button>
                          <button onClick={() => handleDelete(d.id)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-red-50 text-foreground-400 hover:text-red-600 transition-colors cursor-pointer" title="Delete">
                            <i className="ri-delete-bin-line text-sm"></i>
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {filtered.length === 0 && (
                    <tr><td colSpan={8} className="px-4 py-12 text-center text-foreground-400">No domains found matching search</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {showModal && (
          <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50" onClick={() => setShowModal(false)}>
            <div className="bg-background-50 rounded-2xl shadow-xl max-w-md w-full" onClick={(e) => e.stopPropagation()}>
              <div className="border-b border-background-200/70 p-5 flex items-center justify-between">
                <h3 className="font-heading text-lg font-bold text-foreground-950">{editId ? 'Edit Domain' : 'Add Domain'}</h3>
                <button onClick={() => setShowModal(false)} className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 transition-colors cursor-pointer">
                  <i className="ri-close-line text-lg"></i>
                </button>
              </div>
              <div className="p-5 space-y-4">
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Domain Name</label>
                  <input type="text" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" placeholder="e.g. Artificial Intelligence" />
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Description</label>
                  <textarea value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none resize-none" placeholder="Brief description of this domain..."></textarea>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Icon (Remix Icon class)</label>
                  <select value={form.icon} onChange={(e) => setForm({ ...form, icon: e.target.value })} className="w-full px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-700 text-sm focus:border-primary-400 outline-none cursor-pointer">
                    <option value="ri-lightbulb-line">Lightbulb</option>
                    <option value="ri-robot-2-line">Robot</option>
                    <option value="ri-smartphone-line">Smartphone</option>
                    <option value="ri-dna-line">DNA</option>
                    <option value="ri-hospital-line">Hospital</option>
                    <option value="ri-brain-line">Brain</option>
                    <option value="ri-heart-pulse-line">Heart Pulse</option>
                    <option value="ri-mental-health-line">Mental Health</option>
                    <option value="ri-microscope-line">Microscope</option>
                    <option value="ri-computer-line">Computer</option>
                  </select>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Theme Color</label>
                  <div className="flex gap-2">
                    {colorOptions.map((c) => (
                      <button
                        key={c.value}
                        onClick={() => setForm({ ...form, color: c.value })}
                        className={`w-10 h-10 rounded-lg ${c.class} transition-all cursor-pointer ${form.color === c.value ? 'ring-2 ring-offset-2 ring-foreground-400 scale-110' : 'opacity-60 hover:opacity-80'}`}
                        title={c.label}
                      />
                    ))}
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-foreground-600 mb-1">Display Order</label>
                  <input type="number" value={form.sort_order} onChange={(e) => setForm({ ...form, sort_order: parseInt(e.target.value) || 0 })} className="w-24 px-3 py-2 rounded-lg border border-background-200 bg-background-50 text-foreground-900 text-sm focus:border-primary-400 outline-none" min="0" />
                </div>
                <div className="flex items-center gap-3">
                  <label className="flex items-center gap-2 cursor-pointer">
                    <input type="checkbox" checked={form.status} onChange={(e) => setForm({ ...form, status: e.target.checked })} className="w-4 h-4 rounded accent-primary-500" />
                    <span className="text-sm text-foreground-700">Enabled</span>
                  </label>
                </div>
                <div className="flex justify-end gap-3 pt-2">
                  <button onClick={() => setShowModal(false)} className="px-4 py-2 rounded-lg border border-background-200 text-foreground-600 text-sm hover:bg-background-100 transition-colors cursor-pointer whitespace-nowrap">Cancel</button>
                  <button onClick={handleSave} disabled={saving} className="px-5 py-2 rounded-lg bg-primary-500 hover:bg-primary-600 text-background-50 dark:text-foreground-950 font-semibold text-sm transition-colors cursor-pointer whitespace-nowrap">
                    {saving ? 'Saving...' : editId ? 'Save Changes' : 'Add Domain'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </AdminLayout>
    </AuthGuard>
  );
}